import React, { useEffect, useState, useCallback } from "react";
import { useEngagementStore } from "./store";
import { Table, Tooltip, Skeleton, Tag } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { get } from 'lodash'
import {
  CheckCircleOutlined,
  StopOutlined,
  EditOutlined,
  ManOutlined,
  WomanOutlined,
} from "@ant-design/icons";


const MyWorkTable = () => {
  const {
    queryParams,
    setQueryParams,
    engagementData,
    engagementLoading,
    engagementError,
    fetchEngagementDetails,
  } = useEngagementStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
 

  const fetchData = useCallback(() => {

    fetchEngagementDetails({
      agents: get(queryParams, 'agents', null),
      monthCount: get(queryParams, 'monthCount', null),
      durationRangeInMinute: get(queryParams, 'durationRangeInMinute', null),
      callDate: get(queryParams, 'callDate', `[${dayjs().format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}]`),
      engagementType: get(queryParams, 'engagementType', null),
      gender: get(queryParams, 'gender', null),
      pageNumber: currentPage,
      pageSize: pageSize,
    });
  }, [queryParams, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const formatDuration = (seconds) => {
    const duration = moment.duration(seconds, "seconds");
    const minutes = duration.minutes();
    const secs = duration.seconds();
    return `${minutes} min. ${secs} sec.`;
  };

  const columns = [
    {
      title: "Coach Name",
      dataIndex: "coachName",
      key: "coachName",
      align: "center",
      render: (text) => (text ? text : "N/A"),
    },
    {
      title: "Coach E-ID",
      dataIndex: "coachEmployeeId",
      key: "coachEmployeeId",
      align: "center",
      render: (text) => (text ? text : "N/A"),
    },
    {
      title: "CX Phone No.",
      dataIndex: "customerPhoneNumber",
      key: "customerPhoneNumber",
      align: "center",
      render: (text) => (text ? text : "N/A"),
    },
    {
      title: "Cx Gender",
      dataIndex: "customerGender",
      key: "customerGender",
      align: "center",
      render: (text) => {
        if (text?.toLowerCase() === "m") return <Tag icon={<ManOutlined />} color="blue">M</Tag>;
        else if (text?.toLowerCase() === "f") return <Tag icon={<WomanOutlined />} color="pink">F</Tag>;
        else return <Tag icon={<ManOutlined />} color="gray">{text}</Tag>;
      },
    },
    {
      title: "Engagement Type",
      dataIndex: "engagementType",
      key: "engagementType",
      align: "center",
      render: (text) => (text ? text : <Tag color="red">?</Tag>),
    },
    {
      title: "Month Count",
      dataIndex: "runningMonth",
      key: "runningMonth",
      align: "center",
      render: (text) => (text ? text : "N/A"),
    },
    {
      title: "Duration",
      dataIndex: "durationInMinute",
      key: "durationInMinute",
      align: "center",
      render: (text, record) => {
        const durationInSeconds = record.durationInMinute;
        const isRed = durationInSeconds >= 900; // 900 seconds = 15 minutes
        return (
          <Tag bordered={false} color={text ? (isRed ? "red" : "green") : "red"}>
            {text ? formatDuration(durationInSeconds) : "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Scoring Form",
      dataIndex: "isScoringFormFilled",
      key: "isScoringFormFilled",
      align: "center",
      render: (text, record) => {
        const tooltipText = record.isScoringFormFilled
          ? "Scoring Form is filled"
          : "Scoring Form is not filled";
        const icon = record.isScoringFormFilled ? (
          <CheckCircleOutlined style={{ color: "#90EE90", fontSize: "24px" }} />
        ) : (
          <EditOutlined style={{ color: "#ADD8E6", fontSize: "24px" }} />
        );
       
        const scoringUrl = `/recording-feedback/${record.engagementTrackingId}/${record.coachUserId}?tab=questions`;

        return (
          <Tooltip title={tooltipText}>
            <a
              href={scoringUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
              }}
            >
              {icon}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: "Feedback Form",
      dataIndex: "isFeedbackFormFilled",
      key: "isFeedbackFormFilled",
      align: "center",
      render: (text, record) => {
        const tooltipText = record.isFeedbackFormFilled
          ? "Feedback Form is filled"
          : record.isScoringFormFilled
            ? "Feedback Form is not filled"
            : "Scoring Form is not filled";
        const icon = record.isFeedbackFormFilled ? (
          <CheckCircleOutlined style={{ color: "#90EE90", fontSize: "24px" }} />
        ) : record.isScoringFormFilled && record.verdict === 'FAIL' || record.verdict === 'CRITICAL_FAIL' ? (
          <EditOutlined style={{ color: "#ADD8E6", fontSize: "24px" }} />
        ) : (
          <StopOutlined style={{ color: "#FFA07A", fontSize: "24px" }} />
        );
        const feedbackUrl = record.isScoringFormFilled
          ? `/recording-feedback/${record.engagementTrackingId}/${record.coachUserId}?tab=feedback`
          : "#";

        return (
          <Tooltip title={tooltipText}>
            <a
              href={feedbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
              }}
              onClick={
                record.isScoringFormFilled && ['FAIL', "CRITICAL_FAIL"].includes(record.verdict)
                  ? undefined
                  : (event) => event.preventDefault()
              }
            >
              {icon}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      align: "center",
      render: (text) => (
        <span
          style={{
            color: text >= 85 ? "green" : "red",
          }}
        >
          {text !== null && text !== undefined ? text : "N/A"}
        </span>
      ),
    },
    {
      title: "Verdict",
      dataIndex: "verdict",
      key: "verdict",
      align: "center",
      render: (text) => (
        <Tag
          color={
            text ? (text.toLowerCase() === "fail" || text.toLowerCase() === "critical_fail"  ? "red" : "green") : "red"
          }
        >
          {text ? text.toUpperCase() : "N/A"}
        </Tag>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <>
      {engagementLoading ? (
        <Skeleton active />
      ) : engagementError ? (
        <div>Error: {engagementError}</div>
      ) : (
        <Table
          columns={columns}
          dataSource={engagementData.agentDetails || []}
          rowKey="engagementTrackingId"
          pagination={{
            current: currentPage,
            pageSize,
            total: engagementData.paginationTotalCount,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      )}
    </>
  );
};

export default MyWorkTable;
